/**
 * Cognitive Engine · Window & WindowManager
 * =========================================
 * Componenti project-agnostic per rendering finestre.
 *
 * Un consumer host (Forge) registra i "kind" renderer:
 *
 *   <WindowManager renderers={{ explain: ExplainRenderer, task: TaskRenderer }}/>
 *
 * Il WindowManager legge dal windowStore e monta i componenti giusti.
 * Drag/resize sono gestiti qui, non nel renderer.
 */
import React, { useEffect, useRef, useState, useCallback } from "react";
import { windowStore, useWindows } from "./WindowStore";
import "./window-manager.css";

// ── SVG icons inline ────────────────────────────────────
const Icon = {
  minimize: <svg viewBox="0 0 12 12" width="12" height="12"><path d="M2 6h8" stroke="currentColor" strokeWidth="1.6"/></svg>,
  close:    <svg viewBox="0 0 12 12" width="12" height="12"><path d="M2 2l8 8M10 2l-8 8" stroke="currentColor" strokeWidth="1.6"/></svg>,
  drag:     <svg viewBox="0 0 12 12" width="12" height="12"><circle cx="3" cy="3" r="1"/><circle cx="9" cy="3" r="1"/><circle cx="3" cy="9" r="1"/><circle cx="9" cy="9" r="1"/></svg>,
};

// ══════════════════════════════════════════════════════════
// Single Window
// ══════════════════════════════════════════════════════════
function Window({ win, RendererComponent }) {
  const [dragging, setDragging] = useState(null); // { offsetX, offsetY }
  const [resizing, setResizing] = useState(null);
  const ref = useRef(null);

  const onHeaderMouseDown = (e) => {
    // Ignore clicks on buttons inside header
    if (e.target.closest("button")) return;
    e.preventDefault();
    setDragging({
      offsetX: e.clientX - win.rect.x,
      offsetY: e.clientY - win.rect.y,
    });
    windowStore.focus(win.id);
  };

  const onResizeMouseDown = (e) => {
    e.preventDefault(); e.stopPropagation();
    setResizing({ startX: e.clientX, startY: e.clientY, w0: win.rect.w, h0: win.rect.h });
  };

  useEffect(() => {
    if (!dragging && !resizing) return;
    const onMove = (e) => {
      if (dragging) {
        windowStore.move(win.id, {
          x: Math.max(0, e.clientX - dragging.offsetX),
          y: Math.max(50, e.clientY - dragging.offsetY),
        });
      } else if (resizing) {
        windowStore.resize(win.id, {
          w: Math.max(280, resizing.w0 + (e.clientX - resizing.startX)),
          h: Math.max(180, resizing.h0 + (e.clientY - resizing.startY)),
        });
      }
    };
    const onUp = () => { setDragging(null); setResizing(null); };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, [dragging, resizing, win.id]);

  if (win.minimized) return null;

  return (
    <div
      ref={ref}
      className={`ce-window ${win.focused ? "focused" : ""}`}
      data-testid={`ce-window-${win.id}`}
      onMouseDown={() => windowStore.focus(win.id)}
      style={{
        left: win.rect.x, top: win.rect.y,
        width: win.rect.w, height: win.rect.h,
        zIndex: 100 + win.z,
      }}
    >
      <div className="ce-window-header" onMouseDown={onHeaderMouseDown}
           data-testid={`ce-window-header-${win.id}`}>
        <span className="ce-window-drag">{Icon.drag}</span>
        <span className="ce-window-title">{win.title}</span>
        <span className="ce-window-kind">{win.kind}</span>
        <button className="ce-window-btn" onClick={() => windowStore.minimize(win.id)}
                data-testid={`ce-window-minimize-${win.id}`} title="Minimizza">
          {Icon.minimize}
        </button>
        <button className="ce-window-btn" onClick={() => windowStore.close(win.id)}
                data-testid={`ce-window-close-${win.id}`} title="Chiudi">
          {Icon.close}
        </button>
      </div>
      <div className="ce-window-body" data-testid={`ce-window-body-${win.id}`}>
        {RendererComponent
          ? <RendererComponent {...win.props} windowId={win.id}/>
          : <FallbackRenderer kind={win.kind} props={win.props}/>}
      </div>
      <div className="ce-window-resize" onMouseDown={onResizeMouseDown}/>
    </div>
  );
}

function FallbackRenderer({ kind, props }) {
  return (
    <div style={{ padding: 16, color: "#a7b0d6", fontSize: 12 }}>
      <div style={{ marginBottom: 8, color: "#7a83a8" }}>Nessun renderer registrato per <b>{kind}</b>.</div>
      <pre style={{ fontSize: 10.5, color: "#cfd6f4", background: "rgba(0,0,0,0.3)",
                    padding: 8, borderRadius: 6, overflow: "auto" }}>
{JSON.stringify(props, null, 2)}
      </pre>
    </div>
  );
}

// ══════════════════════════════════════════════════════════
// Manager (host consumer)
// ══════════════════════════════════════════════════════════
export function WindowManager({ renderers = {} }) {
  const state = useWindows();
  return (
    <div className="ce-window-manager" data-testid="ce-window-manager">
      {state.windows.map((w) => (
        <Window key={w.id} win={w} RendererComponent={renderers[w.kind]}/>
      ))}
      <MinimizedTray windows={state.windows.filter((w) => w.minimized)}/>
    </div>
  );
}

function MinimizedTray({ windows }) {
  if (windows.length === 0) return null;
  return (
    <div className="ce-tray" data-testid="ce-tray">
      {windows.map((w) => (
        <button key={w.id} onClick={() => windowStore.minimize(w.id)}
                className="ce-tray-btn"
                data-testid={`ce-tray-btn-${w.id}`}>
          {w.title}
        </button>
      ))}
    </div>
  );
}

// ══════════════════════════════════════════════════════════
// Toolbar preset layouts
// ══════════════════════════════════════════════════════════
export function WindowToolbar() {
  const state = useWindows();
  const [openCount, setOpenCount] = useState(0);
  useEffect(() => { setOpenCount(state.windows.length); }, [state.windows.length]);

  return (
    <div className="ce-toolbar" data-testid="ce-window-toolbar">
      <span className="ce-toolbar-label">Windows · {openCount}</span>
      <button onClick={() => windowStore.arrange("split-h")}
              data-testid="ce-layout-split-h">◧ Split H</button>
      <button onClick={() => windowStore.arrange("split-v")}
              data-testid="ce-layout-split-v">▤ Split V</button>
      <button onClick={() => windowStore.arrange("grid-2x2")}
              data-testid="ce-layout-grid">▦ Grid 2×2</button>
      <button onClick={() => windowStore.arrange("stack")}
              data-testid="ce-layout-stack">◈ Stack</button>
      <button onClick={() => windowStore.closeAll()}
              className="danger"
              data-testid="ce-layout-close-all">✕ Close all</button>
    </div>
  );
}

// Export helper hook
export { windowStore, useWindows };
